import { PureComponent, Fragment } from 'react';

import { addUFOCustomData } from '@atlaskit/react-ufo/custom-data';
import Spinner from '@atlaskit/spinner';

import { NotFoundState } from 'src/components/error-page/components/not-found-state';
import { Repository } from 'src/components/types';
import { Loading } from 'src/sections/global/components/loading.style';
import { ResultsSourceView } from 'src/sections/search/components/results-source-view/results-source-view';
import * as styles from 'src/styles/index';
import { statsdApiClient } from 'src/utils/metrics';

import DirectoryError from '../components/directory-error';
import FileError from '../components/file-error';
import { FilterMode } from '../constants';
import SourceDirectoryView from '../containers/directory';
import SourceFileView from '../containers/file';
import FilteredFilesList from '../containers/file-filter-list';
import { FilteredFiles, SourceObject } from '../types';

import FilterHeader from './filter-header';

export type SourceBrowserProps = {
  filteredFiles: FilteredFiles;
  isDirectory: boolean;
  loadSourceObject: () => any;
  repository: Repository;
  sourceObject: SourceObject | null | undefined;
  filterMode: FilterMode;
  filterQuery?: string;
};

// eslint-disable-next-line import/no-default-export
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export default class SourceBrowser extends PureComponent<SourceBrowserProps> {
  render() {
    const {
      filteredFiles,
      isDirectory,
      repository,
      sourceObject,
      filterMode,
      filterQuery,
    } = this.props;

    addUFOCustomData({ isSourceDirectory: isDirectory });

    // Only show filtered files/code if we are in a directory.
    // The filter is cleared when the file list unmounts, so if we clicked on a
    // file, we need to not render the filtered list/code
    if (isDirectory && filterQuery) {
      if (filterMode === FilterMode.Files) {
        return filteredFiles ? <FilteredFilesList /> : null;
      } else {
        return (
          <ResultsSourceView query={filterQuery} resultsHeader={FilterHeader} />
        );
      }
    }

    if (!sourceObject || sourceObject.isLoading) {
      return (
        <Loading>
          <Spinner size="large" />
        </Loading>
      );
    }

    const { treeEntry } = sourceObject;
    if (!treeEntry) {
      let errorContent;
      if (sourceObject.errorCode === 404) {
        errorContent = <NotFoundState />;
      } else {
        errorContent = isDirectory ? (
          <DirectoryError onClick={this.props.loadSourceObject} />
        ) : (
          <FileError onClick={this.props.loadSourceObject} />
        );

        statsdApiClient.increment('frontbucket.source.browser.error', {
          tags: [isDirectory ? 'type:directory' : 'type:file'],
        });
      }

      return (
        <Fragment>
          <styles.HorizontalRule />
          {errorContent}
        </Fragment>
      );
    }

    const sourcePath = sourceObject.path;
    const sourceHash = sourceObject.hash;

    if (treeEntry.type === 'directory') {
      return (
        <SourceDirectoryView
          contents={treeEntry.contents}
          hash={sourceHash}
          path={sourcePath}
          repository={repository}
        />
      );
    }
    return (
      <SourceFileView
        hash={sourceHash}
        path={sourcePath || treeEntry.name}
        repository={repository}
      />
    );
  }
}
